import { createContext, useContext, useState, useCallback, useMemo, useEffect, type ReactNode } from "react";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { useMarketingAssets, type MarketingAssetsData } from "../hooks/useMarketingAssets";
import { MarketingAsset } from "@/bryntum-lib/models/MarketingAsset";
import { MarketingTag, MarketingTagType } from "@/bryntum-lib/models/MarketingTag";
import { EDITOR_KEYS } from "@/other/constants/EditorKeys";
import { EntityRecord } from "@/types/entity";
import { showToast } from "@/other/utils/utils";
import {
  demoteAcceptedMarketingAsset,
  invalidateMarketingAssets,
} from "../api/marketingAssets.api";
import { ENTITY_RECORD_FIELD_TO_API } from "../api/marketingAssets.query-keys";
import type { SwivuMarketingAsset } from "../api/marketingAssets.types";

type TagFilter = "all" | number;
type VisibilityFilter = "all" | "public" | "restricted";

interface MarketingEditorContextValue extends MarketingAssetsData {
  entityRecord: EntityRecord;

  selectedAssetId: number | null;
  selectedAsset: SwivuMarketingAsset | null;
  selectAsset: (id: number) => void;

  temporalFilter: TagFilter;
  setTemporalFilter: (filter: TagFilter) => void;
  tagFilter: TagFilter;
  setTagFilter: (filter: TagFilter) => void;
  visibilityFilter: VisibilityFilter;
  setVisibilityFilter: (filter: VisibilityFilter) => void;

  tagsWithAssets: MarketingTag[];
  temporalTags: MarketingTag[];
  folderTags: MarketingTag[];
  contentTags: MarketingTag[];

  filteredAssets: SwivuMarketingAsset[];

  deleteAsset: (asset: SwivuMarketingAsset) => Promise<void>;
  demoting: boolean;
  openFile: (asset: SwivuMarketingAsset) => void;

  toggleAssetTag: (tagId: number) => void;
  updateAssetField: <K extends keyof SwivuMarketingAsset>(field: K, value: SwivuMarketingAsset[K]) => void;

  getTemporalTag: (asset: SwivuMarketingAsset) => MarketingTag | undefined;
  getFolderTags: (asset: SwivuMarketingAsset) => MarketingTag[];
  getContentTags: (asset: SwivuMarketingAsset) => MarketingTag[];
  getProblemTags: (asset: SwivuMarketingAsset) => MarketingTag[];

  onCloseEditor: () => void;
}

const MarketingEditorContext = createContext<MarketingEditorContextValue | undefined>(undefined);

interface MarketingEditorProviderProps {
  children: ReactNode;
  entityRecord: EntityRecord;
  onCloseEditor: () => void;
}

export const MarketingEditorProvider = ({ children, entityRecord, onCloseEditor }: MarketingEditorProviderProps) => {
  const { project, editorHandler, selectedBoard } = useSwivuBryntumContext();
  const marketingData = useMarketingAssets(entityRecord);
  const { assets, allTags } = marketingData;

  const [selectedAssetId, setSelectedAssetId] = useState<number | null>(null);
  const [temporalFilter, setTemporalFilter] = useState<TagFilter>("all");
  const [tagFilter, setTagFilter] = useState<TagFilter>("all");
  const [visibilityFilter, setVisibilityFilter] = useState<VisibilityFilter>("all");
  const [demoting, setDemoting] = useState(false);

  const tagsWithAssets = useMemo(() => {
    const tagIdsInUse = new Set(assets.flatMap((a) => a.tags ?? []));
    return allTags.filter((t) => tagIdsInUse.has(t.id));
  }, [assets, allTags]);

  const temporalTags = useMemo(
    () => allTags.filter((t) => t.type === MarketingTagType.TEMPORAL),
    [allTags],
  );

  const folderTags = useMemo(
    () => allTags.filter((t) => t.type === MarketingTagType.CONTENT || t.isFolderEligible),
    [allTags],
  );

  const contentTags = useMemo(
    () => allTags.filter((t) => t.type === MarketingTagType.SOLUTION),
    [allTags],
  );

  const filteredAssets = useMemo(() => {
    let result = assets;
    if (temporalFilter !== "all") {
      result = result.filter((a) => a.tags?.includes(temporalFilter as number));
    }
    if (tagFilter !== "all") {
      result = result.filter((a) => a.tags?.includes(tagFilter as number));
    }
    if (visibilityFilter === "public") {
      result = result.filter((a) => !a.isRestricted);
    } else if (visibilityFilter === "restricted") {
      result = result.filter((a) => a.isRestricted);
    }
    return result;
  }, [assets, temporalFilter, tagFilter, visibilityFilter]);

  const selectedAsset = useMemo(
    () =>
      selectedAssetId != null ? (filteredAssets.find((a) => a.id === selectedAssetId) ?? null) : null,
    [filteredAssets, selectedAssetId],
  );

  useEffect(() => {
    if (selectedAssetId == null) return;
    if (!filteredAssets.some((a) => a.id === selectedAssetId)) {
      setSelectedAssetId(null);
    }
  }, [filteredAssets, selectedAssetId]);

  const selectAsset = useCallback((id: number) => setSelectedAssetId(id), []);

  const getStoreAsset = useCallback(
    (assetId: number): MarketingAsset | null => {
      const record = project?.marketingAssetStore?.getById(assetId);
      return (record as MarketingAsset | undefined) ?? null;
    },
    [project],
  );

  const invalidateEntityAssets = useCallback(async () => {
    const boardId = selectedBoard?.id;
    const entityType = ENTITY_RECORD_FIELD_TO_API[entityRecord.entityType];
    if (!boardId || !entityType) return;
    await invalidateMarketingAssets(boardId, entityType, Number(entityRecord.id));
  }, [selectedBoard?.id, entityRecord]);

  const deleteAsset = useCallback(
    async (asset: SwivuMarketingAsset) => {
      const slug = (selectedBoard?.record as { slug?: string } | undefined)?.slug;
      const boardId = selectedBoard?.id;
      const entityType = ENTITY_RECORD_FIELD_TO_API[entityRecord.entityType];
      if (!slug || boardId == null || !entityType) {
        showToast({ msg: "No board selected.", type: "error", duration: 3000 });
        throw new Error("Board slug is not available");
      }

      setDemoting(true);
      try {
        await demoteAcceptedMarketingAsset({
          slug,
          assetId: asset.id,
          boardId,
          entityType,
          entityId: Number(entityRecord.id),
        });
        // Keep Bryntum store aligned with demotion for other local readers.
        project?.marketingAssetStore?.remove(asset.id);
        const remaining = assets.filter((a) => a.id !== asset.id);
        if (remaining.length === 0) {
          onCloseEditor();
          return;
        }
        if (selectedAssetId === asset.id) {
          setSelectedAssetId(null);
        }
      } catch {
        showToast({ msg: "Failed to demote asset.", type: "error", duration: 3000 });
        throw new Error("Demote failed");
      } finally {
        setDemoting(false);
      }
    },
    [
      selectedBoard,
      entityRecord,
      project,
      assets,
      selectedAssetId,
      onCloseEditor,
    ],
  );

  const openFile = useCallback(
    (_asset: SwivuMarketingAsset) => {
      const entityType = ENTITY_RECORD_FIELD_TO_API[entityRecord.entityType] ?? "task";
      editorHandler.openEditor(
        EDITOR_KEYS.taskFilesEditor,
        entityRecord,
        { entity: entityRecord, entityType },
        { by: "recordType", useExisting: true },
      );
    },
    [editorHandler, entityRecord],
  );

  const toggleAssetTag = useCallback(
    (tagId: number) => {
      if (!selectedAsset) return;
      const storeAsset = getStoreAsset(selectedAsset.id);
      if (!storeAsset) return;
      const current: number[] = storeAsset.tags ?? [];
      storeAsset.tags = current.includes(tagId)
        ? current.filter((id) => id !== tagId)
        : [...current, tagId];
      void invalidateEntityAssets();
    },
    [selectedAsset, getStoreAsset, invalidateEntityAssets],
  );

  const updateAssetField = useCallback(
    <K extends keyof SwivuMarketingAsset>(field: K, value: SwivuMarketingAsset[K]) => {
      if (!selectedAsset) return;
      const storeAsset = getStoreAsset(selectedAsset.id);
      if (!storeAsset) return;
      // Bryntum model fields align with SwivuMarketingAsset camelCase names.
      (storeAsset as any)[field] = value;
      void invalidateEntityAssets();
    },
    [selectedAsset, getStoreAsset, invalidateEntityAssets],
  );

  const getTemporalTag = useCallback(
    (asset: SwivuMarketingAsset) =>
      temporalTags.find((t) => asset.tags?.includes(t.id)),
    [temporalTags],
  );

  const getFolderTags = useCallback(
    (asset: SwivuMarketingAsset) =>
      allTags.filter((t) => t.isFolderEligible && asset.tags?.includes(t.id)),
    [allTags],
  );

  const getContentTags = useCallback(
    (asset: SwivuMarketingAsset) =>
      allTags.filter((t) => t.type === MarketingTagType.SOLUTION && asset.tags?.includes(t.id)),
    [allTags],
  );

  const getProblemTags = useCallback(
    (asset: SwivuMarketingAsset) =>
      allTags.filter((t) => t.type === MarketingTagType.PROBLEM && asset.tags?.includes(t.id)),
    [allTags],
  );

  const value: MarketingEditorContextValue = {
    ...marketingData,
    entityRecord,
    selectedAssetId,
    selectedAsset,
    selectAsset,
    temporalFilter,
    setTemporalFilter,
    tagFilter,
    setTagFilter,
    visibilityFilter,
    setVisibilityFilter,
    tagsWithAssets,
    temporalTags,
    folderTags,
    contentTags,
    filteredAssets,
    deleteAsset,
    demoting,
    openFile,
    toggleAssetTag,
    updateAssetField,
    getTemporalTag,
    getFolderTags,
    getContentTags,
    getProblemTags,
    onCloseEditor,
  };

  return (
    <MarketingEditorContext.Provider value={value}>
      {children}
    </MarketingEditorContext.Provider>
  );
};

export const useMarketingEditorContext = () => {
  const ctx = useContext(MarketingEditorContext);
  if (!ctx) throw new Error("useMarketingEditorContext must be used within MarketingEditorProvider");
  return ctx;
};
